import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {NucleotideType} from 'biojs/Util';
import {AllPolymerLayouts} from 'constants/MapsConstants';
import {ModifiedNucleotideAttributes} from 'constants/SequenceConstants';
import {SequenceType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {
  ModifiedNucleotidesQuery,
  ModifiedNucleotidesQueryVariables,
} from 'molbio/monomers/__generated/ModifiedNucleotidesQuery';
import {MonomerQuery} from 'molbio/monomers/__generated/MonomerQuery';
import {
  displayAsNatural,
  getDisplayModsForSlice,
  getVisualSymbolForPhosphateMonomer,
  letterCenterX,
} from 'molbio/monomers/ModificationVisualizationUtil';
import ModifiedBase from 'molbio/monomers/ModifiedBase';
import ModifiedPhosphate from 'molbio/monomers/ModifiedPhosphate';
import ModifiedSugar from 'molbio/monomers/ModifiedSugar';
import {
  BASE_BACKGROUND_COLOR,
  DEFAULT_MODIFIED_BASE_COLOR,
  DEFAULT_MODIFIED_SUGAR_COLOR,
  MONOMER_QUERY,
  UNMODIFIED_BASE_COLOR,
} from 'molbio/monomers/MonomerConstants';
import {MODIFIED_NUCLEOTIDES_QUERY} from 'molbio/monomers/NucleotidesTab';
import Popover from 'ui-library/floating-ui/Popover';

import {ModifiedNucleotideGraphQL} from '../../../../graphql-fragments/modbio/__generated/ModifiedNucleotideGraphQL';
import ComponentWrapper from './ComponentWrapper';
import {widthBetweenLetters} from './LinearMapUtil';
import ModifiedNucleotideTooltipContents from './ModifiedNucleotideTooltipContents';

type Monomer = MonomerQuery['monomers'][number];

const UNMODIFIED_FILL_OPACITY = 0.05;
const MODIFIED_FILL_OPACITY = 0.0001;
const BACKGROUND_OPACITY = 1;
const BACKGROUND_BORDER = '#D8DFED';
// Arbitrary widthBetweenLetters at which we make the circles smaller to look less cluttered
const SHIFT_THRESHOLD = 28;
export const MODIFIED_NUCLEOTIDES_BOX_HEIGHT = 10;
const LETTER_CENTER_Y_OFFSET = MODIFIED_NUCLEOTIDES_BOX_HEIGHT - 2;

// The width of a modified phosphate
export const PHOSPHATE_WIDTH = 3;
// The height of a modified phosphate
export const PHOSPHATE_HEIGHT = 3;

function letterCenterY(xFn: (index: number) => number, sliceStart: number, boxHeightToDNA: number): number {
  return LETTER_CENTER_Y_OFFSET + boxHeightToDNA + 0.5 * widthBetweenLetters(xFn, sliceStart);
}

function phosphateSymbolOffsetX(
  xFn: (index: number) => number,
  sliceStart: number,
  isForwardStrand: boolean
): number {
  const forwardPhosphateOffset = 0.25 * widthBetweenLetters(xFn, sliceStart) + 2;
  return isForwardStrand
    ? forwardPhosphateOffset
    : -forwardPhosphateOffset - phosphateSymbolSize(xFn, sliceStart);
}

function phosphateSymbolOffsetY(
  xFn: (index: number) => number,
  sliceStart: number,
  isForwardStrand: boolean
): number {
  const reversePhosphateOffset = 0.125 * widthBetweenLetters(xFn, sliceStart) + LETTER_CENTER_Y_OFFSET;
  return isForwardStrand ? -reversePhosphateOffset - 4 : reversePhosphateOffset - 4;
}

function phosphateSymbolSize(xFn: (index: number) => number, sliceStart: number): number {
  return 0.25 * widthBetweenLetters(xFn, sliceStart);
}

function outerRadiusRatio(xFn: (index: number) => number, sliceStart: number): number {
  return widthBetweenLetters(xFn, sliceStart) < SHIFT_THRESHOLD ? 0.45 : 0.4;
}

function innerRadiusRatio(xFn: (index: number) => number, sliceStart: number): number {
  return widthBetweenLetters(xFn, sliceStart) < SHIFT_THRESHOLD ? 0.35 : 0.3;
}

function outerRadius(xFn: (index: number) => number, sliceStart: number): number {
  return outerRadiusRatio(xFn, sliceStart) * widthBetweenLetters(xFn, sliceStart);
}

export function modifiedNucleotideVisualizationHeight(
  xFn: (index: number) => number,
  sliceStart: number,
  boxHeightToDNA: number
): number {
  // The center of the base is positioned here, relative to the top of the container element
  const baseCenterYPosition = letterCenterY(xFn, sliceStart, boxHeightToDNA);
  // The top of the base is 'r' away from the center of the base
  const baseRadius = outerRadius(xFn, sliceStart);
  const baseTopYPosition = baseCenterYPosition - baseRadius;

  // The total height of the element containing the base and vertical whitespace is the position of the top
  // of the base + the height of the base
  return baseTopYPosition + baseRadius * 2;
}

export function modifiedNucleotideVMargin(
  xFn: (index: number) => number,
  sliceStart: number,
  boxHeightToDNA: number
): number {
  // The center of the base is positioned here relative to the top of the container element
  const baseCenterYPosition = letterCenterY(xFn, sliceStart, boxHeightToDNA);
  // The top of the base is 'r' away from the center of the base
  // The distance from the top of the container element to the top of the base is the vertical whitespace
  const baseRadius = outerRadius(xFn, sliceStart);
  return baseCenterYPosition - baseRadius;
}

interface SharedModifiedNucleotidesPropsInterface {
  xFn: (index: number) => number;
  sliceStart: number;
  sliceEnd: number;
  bases: string;
  boxHeightToDNA: number;
  showNucleotideStructures: boolean;
}

interface DisplayModProps {
  mod: ModifiedNucleotideAttributes;
  isOligo: boolean;
  monomers: Array<Monomer>;
  nucleotideType: NucleotideType;
  xFn: (index: number) => number;
  sliceStart: number;
  showNucleotideStructures: boolean;
  isForwardStrand?: boolean;
  displayBasesAsNatural?: boolean;
}

function DisplayMod({
  mod,
  isOligo,
  monomers,
  nucleotideType,
  xFn,
  sliceStart,
  isForwardStrand,
  displayBasesAsNatural,
}: DisplayModProps): JSX.Element {
  const baseMonomer = monomers.find((monomer) => monomer.id === mod.baseMonomerId);
  const displayBaseNatural =
    (displayBasesAsNatural as boolean) ||
    displayAsNatural(mod.baseMonomerId, monomers, nucleotideType, isOligo);
  const sugarMonomer = monomers.find((monomer) => monomer.id === mod.sugarMonomerId);
  const displaySugarNatural = displayAsNatural(mod.sugarMonomerId, monomers, nucleotideType, isOligo);
  const phosphateMonomer = monomers.find((monomer) => monomer.id === mod.phosphateMonomerId);
  const displayPhosphateNatural = displayAsNatural(mod.phosphateMonomerId, monomers, nucleotideType, isOligo);
  return (
    <g key={`${mod.position}-${mod.sugarMonomerId}-${mod.baseMonomerId}-${mod.phosphateMonomerId}`}>
      {!displayBaseNatural && (
        <ModifiedBase
          fill={baseMonomer?.color ?? DEFAULT_MODIFIED_BASE_COLOR}
          // If there is also a modified sugar, make modified base circle smaller to accommodate.
          // Otherwise, make modified base circle same size as modified sugar circle.
          radius={
            !displaySugarNatural
              ? innerRadiusRatio(xFn, sliceStart) * widthBetweenLetters(xFn, sliceStart)
              : outerRadius(xFn, sliceStart)
          }
        />
      )}
      {!displaySugarNatural && (
        <ModifiedSugar
          radius={outerRadius(xFn, sliceStart)}
          stroke={sugarMonomer?.color ?? DEFAULT_MODIFIED_SUGAR_COLOR}
        />
      )}
      {!displayPhosphateNatural && (
        <ModifiedPhosphate
          size={phosphateSymbolSize(xFn, sliceStart)}
          offsetX={phosphateSymbolOffsetX(xFn, sliceStart, isForwardStrand ?? true)}
          offsetY={phosphateSymbolOffsetY(xFn, sliceStart, isForwardStrand ?? true)}
          symbol={getVisualSymbolForPhosphateMonomer(phosphateMonomer)}
        />
      )}
    </g>
  );
}

export interface ModifiedNucleotidesVisualizationProps extends SharedModifiedNucleotidesPropsInterface {
  layout: {vMargin: number; visible?: boolean};
  isOligo: boolean;
  sequenceMods: Array<ModifiedNucleotideAttributes>;
  monomers: Array<Monomer>;
  nucleotideType: NucleotideType;
  keyPrefix: string;
  // Set to false if on the reverse strand of an oligo duplex.
  isForwardStrand?: boolean;
  // Whether component is wrapped in G element, as opposed to SVG element. Returns as SVG if undefined.
  shouldReturnG?: boolean;
  displayBasesAsNatural?: boolean;
  includeBackground?: boolean;
  showOverflow?: boolean;
}

export function ModifiedNucleotidesVisualization({
  isOligo,
  sequenceMods,
  monomers,
  nucleotideType,
  xFn,
  sliceStart,
  sliceEnd,
  keyPrefix,
  bases,
  layout,
  boxHeightToDNA,
  showNucleotideStructures,
  isForwardStrand,
  shouldReturnG,
  displayBasesAsNatural,
  includeBackground,
  showOverflow = false,
}: ModifiedNucleotidesVisualizationProps): JSX.Element {
  const displayMods = getDisplayModsForSlice(
    monomers,
    nucleotideType,
    bases,
    sequenceMods,
    sliceStart
  ).filter((mod: ModifiedNucleotideGraphQL) => mod.position >= sliceStart && mod.position < sliceEnd);
  const slicePositions = [];
  for (let i = sliceStart; i < sliceEnd; i++) {
    slicePositions.push(i);
  }

  function getCircleFillOpacity(mod: ModifiedNucleotideAttributes | null): number {
    if (mod != null) {
      if (
        !displayAsNatural(mod.baseMonomerId, monomers, nucleotideType, isOligo) ||
        !displayAsNatural(mod.sugarMonomerId, monomers, nucleotideType, isOligo)
      ) {
        return MODIFIED_FILL_OPACITY;
      }
    }
    return UNMODIFIED_FILL_OPACITY;
  }

  const modifiedNucleotidesVisualization = (
    <ComponentWrapper layout={layout} shouldStopMouseDownPropagation={false}>
      <g>
        {slicePositions.map((slicePosition: number, index: number) => {
          const mod = displayMods.find(({position}) => position === slicePosition) ?? null;
          const outerRadiusSize = outerRadius(xFn, sliceStart);
          return (
            <g
              // This is the translate to update
              key={slicePosition}
              transform={`translate(${letterCenterX(xFn, sliceStart, slicePosition)},
                  ${letterCenterY(xFn, sliceStart, boxHeightToDNA)})`}
            >
              <Popover
                key={`${keyPrefix}-${slicePosition}`}
                renderContent={() => (
                  <ModifiedNucleotideTooltipContents
                    mod={mod}
                    monomers={monomers}
                    base={bases[index]}
                    nucleotideType={nucleotideType}
                    showNucleotideStructure={showNucleotideStructures}
                  />
                )}
              >
                <g>
                  {/* Hack: if there is a modified base or sugar, then we do not want the lightly shaded gray circle.
                  However, if there is no fill to the circle, the tooltip will not appear over it, so we fill it in
                  with trivial fill opacity. */}
                  <circle
                    cx={0}
                    cy={0}
                    r={outerRadiusSize}
                    fill={UNMODIFIED_BASE_COLOR}
                    fillOpacity={getCircleFillOpacity(mod)}
                  />
                  {includeBackground && (
                    <circle
                      key={slicePosition}
                      id="baseBackground"
                      cx={0}
                      cy={0}
                      fill={BASE_BACKGROUND_COLOR}
                      fillOpacity={BACKGROUND_OPACITY}
                      r={outerRadiusSize}
                      shapeRendering="geometricPrecision"
                      stroke={BACKGROUND_BORDER}
                      strokeWidth={0.5}
                    />
                  )}
                  {mod != null && (
                    <DisplayMod
                      mod={mod}
                      isOligo={isOligo}
                      monomers={monomers}
                      nucleotideType={nucleotideType}
                      xFn={xFn}
                      sliceStart={sliceStart}
                      showNucleotideStructures={showNucleotideStructures}
                      isForwardStrand={isForwardStrand}
                      displayBasesAsNatural={displayBasesAsNatural}
                    />
                  )}
                </g>
              </Popover>
            </g>
          );
        })}
      </g>
    </ComponentWrapper>
  );
  return shouldReturnG ? (
    modifiedNucleotidesVisualization
  ) : (
    <svg
      className={classNames({
        [css(styles.svgOverflowVisible)]: showOverflow,
      })}
    >
      {modifiedNucleotidesVisualization}
    </svg>
  );
}

export interface ModifiedNucleotidesProps extends SharedModifiedNucleotidesPropsInterface {
  layout: AllPolymerLayouts['modifiedNucleotides'];
  sequenceId: string;
}

export default function ModifiedNucleotides({
  xFn,
  sliceStart,
  sliceEnd,
  sequenceId,
  bases,
  layout,
  boxHeightToDNA,
  showNucleotideStructures,
}: ModifiedNucleotidesProps): JSX.Element | null {
  if (!layout.visible) {
    return null;
  }
  return (
    <BenchlingQuery<MonomerQuery> query={MONOMER_QUERY} renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}>
      {(monomerData) => (
        // Hack: fetch monomers first to populate the cache, to make MODIFIED_NUCLEOTIDES_QUERY work
        <BenchlingQuery<ModifiedNucleotidesQuery, ModifiedNucleotidesQueryVariables>
          query={MODIFIED_NUCLEOTIDES_QUERY}
          renderLoading={BenchlingQuery.DEFAULT_RENDER_LOADING}
          variables={{sequenceId}}
          fetchPolicy="cache-only"
        >
          {(data) => {
            const isOligo = data.componentSequence!.type === SequenceType.oligo;
            const modifications = data.componentSequence!.modifiedNucleotides;
            const nucleotideType = data.componentSequence!.nucleotideType;
            return (
              <ModifiedNucleotidesVisualization
                isOligo={isOligo}
                sequenceMods={modifications}
                monomers={monomerData.monomers}
                nucleotideType={nucleotideType}
                xFn={xFn}
                sliceStart={sliceStart}
                sliceEnd={sliceEnd}
                keyPrefix={sequenceId}
                bases={bases}
                layout={layout}
                boxHeightToDNA={boxHeightToDNA}
                showNucleotideStructures={showNucleotideStructures}
              />
            );
          }}
        </BenchlingQuery>
      )}
    </BenchlingQuery>
  );
}

const styles = StyleSheet.create({
  svgOverflowVisible: {
    overflow: 'visible',
  },
});
